import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, CheckCircle, RefreshCw } from "lucide-react";
import { apiGet, apiGetBlob, apiPost, apiDelete } from "../Api/Client.js";
import { discardRecorder, replaceSample, sampleSource } from "./voiceSamples.js";

const LANGUAGES = [
  {
    key: "en",
    label: "English",
    prompt: (name) =>
      `Hello, My Name Is ${name}. Today I Am Recording This Voice Sample For Speaker Identification. The Quick Brown Fox Jumps Over The Lazy Dog. I Speak Naturally And Clearly.`,
  },
  {
    key: "regional",
    label: "Regional Language",
    prompt: (name) =>
      `Read A Natural Sentence In Your Regional Language. Example : \nनमस्ते, मेरा नाम ${name} है। मैं यह आवाज़ का नमूना रिकॉर्ड कर रहा हूँ ताकि सिस्टम भविष्य की बैठकों में मेरी पहचान कर सके। मैं सामान्य गति से और स्पष्ट रूप से बोल रहा हूँ।`,
  },
];

export default function VoiceRecorder({ employeeId, employeeName, onChange }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const audioRef = useRef(null);
  const name = employeeName || "User";

  const fetchRecordings = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/Users/VoiceRecordings/?employee=${employeeId}`);
      setRecordings(Array.isArray(data) ? data : data?.results || []);
      setLoadFailed(false);
    } catch {
      // Not an empty list: showing "no samples" would invite recording duplicates.
      setLoadFailed(true);
    } finally {
      setLoading(false);
      if (onChange) onChange();
    }
  };

  useEffect(() => {
    if (employeeId) fetchRecordings(); else setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    return () => {
      discardRecorder(mediaRecorder.current);
      stopAudio();
    };
  }, []);

  // Stops the sample that is playing and frees its local copy.
  const stopAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    if (audio.src.startsWith("blob:")) URL.revokeObjectURL(audio.src);
    audioRef.current = null;
  };

  const hasEnglish = recordings.some((r) => r.language === "en");
  const hasRegional = recordings.some((r) => r.language === "regional");
  const bothDone = hasEnglish && hasRegional;

  const startRecording = async (lang) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorder.current = recorder;
      // The samples of this language that exist now are replaced only once the new one is saved.
      const previousIds = recordings.filter((r) => r.language === lang).map((r) => r.id);
      chunks.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: recorder.mimeType });
        await uploadRecording(blob, lang, previousIds);
      };
      recorder.start();
      setActiveLang(lang);
    } catch {
      alert("Microphone Access Denied Or Not Available.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
      setActiveLang(null);
    }
  };

  const uploadRecording = async (blob, lang, previousIds = []) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("employee", employeeId);
      fd.append("language", lang);
      fd.append("title", `${lang === "en" ? "English" : "Regional"} Voice Sample - ${new Date().toLocaleDateString()}`);
      fd.append("audio_file", blob, `recording_${lang}_${Date.now()}.wav`);
      const { stuck } = await replaceSample({
        upload: () => apiPost("/Users/VoiceRecordings/", fd),
        remove: (id) => apiDelete(`/Users/VoiceRecordings/${id}/`),
        previousIds,
      });
      await fetchRecordings();
      if (stuck.length) alert("Your new sample is saved, but the old one could not be removed. Please delete it yourself.");
    } catch (e) {
      alert("Upload Failed. " + (e.message || ""));
    } finally {
      setUploading(false);
    }
  };

  const togglePlay = async (rec) => {
    stopAudio();
    if (playing === rec.id) {
      setPlaying(null);
      return;
    }
    setPlaying(rec.id);
    try {
      const src = await sampleSource(apiGetBlob, rec.audio_url);
      if (!src) { setPlaying(null); return; }
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.onended = () => { stopAudio(); setPlaying(null); };
      audio.play().catch(() => {});
    } catch {
      setPlaying(null);
      alert("This sample could not be played.");
    }
  };

  const deleteRecording = async (id) => {
    if (!window.confirm("Delete This Recording?")) return;
    try {
      await apiDelete(`/Users/VoiceRecordings/${id}/`);
      setRecordings((prev) => prev.filter((r) => r.id !== id));
    } catch {
      alert("Delete Failed.");
    }
  };

  const getRecording = (lang) => recordings.find((r) => r.language === lang);

  return (
    <div style={{ borderTop: "1px solid var(--c-100)", paddingTop: 14, marginTop: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Mic size={16} style={{ color: "var(--c-500)" }} />
        <strong style={{ fontSize: 14, color: "var(--c-900)" }}>Voice Samples</strong>
        {bothDone && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--green-600)", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <CheckCircle size={14} /> Complete
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 12, color: "var(--c-400)", fontSize: 13 }}>Loading...</div>
      ) : loadFailed ? (
        <div style={{ textAlign: "center", padding: 12, color: "var(--c-600)", fontSize: 13 }}>
          Your voice samples could not be loaded.{" "}
          <button className="VR-RecBtn VR-RecBtnOutline" style={{ fontSize: 11, padding: "4px 10px" }} onClick={fetchRecordings}>
            Try again
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {LANGUAGES.map((lang) => {
            const rec = getRecording(lang.key);
            const isRecording = activeLang === lang.key;
            return (
              <div
                key={lang.key}
                className="VR-LangSection"
                style={{
                  border: "1px solid",
                  borderColor: rec ? "var(--green-100)" : isRecording ? "var(--red-200)" : "var(--c-200)",
                  borderRadius: 10,
                  padding: 12,
                  background: rec ? "var(--green-50)" : isRecording ? "var(--red-50)" : "var(--surface-soft)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <strong style={{ fontSize: 13, color: "var(--c-900)" }}>{lang.label}</strong>
                  {rec && <CheckCircle size={13} color="var(--green-600)" />}
                </div>

                <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--c-600)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {lang.prompt(name)}
                </p>

                {isRecording && (
                  <div className="VR-RecordingBadge" style={{ marginBottom: 8 }}>
                    Recording {lang.label.toLowerCase()}...
                  </div>
                )}

                {rec && (
                  <div className="VR-PlayRow">
                    <button className="VR-IconBtn" onClick={() => togglePlay(rec)} style={{ width: 26, height: 26 }}>
                      {playing === rec.id ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <span style={{ fontSize: 12, color: "var(--c-900)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {rec.title}{rec.employee_name ? ` — ${rec.employee_name}` : ""}
                    </span>
                    <button className="VR-IconBtn VR-Danger" onClick={() => deleteRecording(rec.id)} style={{ width: 26, height: 26 }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}

                <div style={{ display: "flex", gap: 6, marginTop: rec || isRecording ? 6 : 0 }}>
                  {!isRecording && (
                    <button
                      className="VR-RecBtn"
                      style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={() => startRecording(lang.key)}
                      disabled={uploading || activeLang !== null}
                    >
                      <Mic size={12} /> {rec ? "Re-record" : "Record"}
                    </button>
                  )}
                  {isRecording && (
                    <button className="VR-StopBtn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={stopRecording}>
                      <Square size={12} /> Stop
                    </button>
                  )}
                  {rec && !isRecording && (
                    <button
                      className="VR-RecBtn VR-RecBtnOutline"
                      style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={() => startRecording(lang.key)}
                      disabled={uploading || activeLang !== null}
                    >
                      <RefreshCw size={12} /> Re-Record
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
